import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { style } from '@vanilla-extract/css';

export const streamsList = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s24,
  marginTop: space.s12,
});

export const streamRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s8,
  width: '100%',
  minHeight: 40,
  padding: `0 ${space.s12}px`,
  borderRadius: borderRadius.r8,
  border: `1px solid ${themeVars.container.border.default}`,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',

  ':hover': {
    backgroundColor: themeVars.controls.background.hover,
  },
});

export const streamName = style([
  {
    color: themeVars.container.foreground.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 0,
    maxWidth: '40%',
  },
  typography.labelMd,
]);

export const chipBadge = style([
  typography.labelSm,
  {
    height: 20,
    padding: `0 ${space.s8}px`,
    gap: space.s4,
  },
]);

export const chipBadgeText = style({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 96,
});

export const countBadge = style([
  chipBadge,
  {
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
]);

export const noShrinkChipBadge = style([
  chipBadge,
  {
    flexShrink: 0,
  },
]);

export const headerSummary = style([
  {
    marginLeft: 'auto',
    color: themeVars.container.foreground.tertiary,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  typography.labelMd,
]);

export const rowRight = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s4,
  marginLeft: 'auto',
  minWidth: 0,
});

export const tooltipTargets = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: space.s4,
  maxWidth: 260,
});

export const tooltipTargetChipText = style([
  chipBadgeText,
  {
    maxWidth: 220,
  },
]);

export const chevron = style({
  color: themeVars.container.foreground.tertiary,
  flexShrink: 0,
  transitionProperty: 'transform',
  transitionDuration: '0.15s',

  selectors: {
    [`${streamRow}[data-state="open"] &`]: {
      transform: 'rotate(180deg)',
    },
  },
});

export const targetsPopover = style({
  width: 'var(--radix-popover-trigger-width)',
  padding: 0,
  overflow: 'hidden',
  border: `1px solid ${themeVars.container.border.default}`,
});

export const popoverHeader = style([
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s8,
    padding: `${space.s8}px ${space.s12}px`,
    color: themeVars.container.foreground.secondary,
  },
  typography.labelSm,
]);

export const popoverFooter = style([
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s8,
    padding: `${space.s8}px ${space.s12}px`,
    borderTop: `1px solid ${themeVars.container.border.default}`,
    color: themeVars.container.foreground.secondary,
  },
  typography.labelSm,
]);

export const targetsList = style({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 240,
  overflowY: 'auto',
});

export const targetsGroup = style({
  display: 'contents',
});

export const streamCardHeader = style([
  streamRow,
  {
    selectors: {
      '&[data-state="open"]': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
  },
]);

export const targetsGrid = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  maxHeight: 240,
  overflowY: 'auto',
});

export const targetRow = style([
  typography.bodySm,
  {
    display: 'flex',
    alignItems: 'center',
    padding: `${space.s4}px ${space.s8}px ${space.s4}px ${space.s12}px`,
    color: themeVars.container.foreground.primary,
  },
]);

export const removedTargetRow = style([
  targetRow,
  {
    color: themeVars.container.foreground.tertiary,
  },
]);

export const targetItem = style([
  {
    display: 'flex',
    alignItems: 'center',
    gap: space.s8,
    padding: `${space.s6}px ${space.s12}px`,
    cursor: 'pointer',
    color: themeVars.container.foreground.primary,

    ':hover': {
      backgroundColor: themeVars.controls.background.hover,
    },
  },
  typography.bodySm,
]);

export const removedGroupHeader = style([
  typography.labelSm,
  {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    gap: space.s8,
    padding: `${space.s8}px ${space.s12}px ${space.s4}px`,
    color: themeVars.container.foreground.tertiary,

    '::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: themeVars.container.border.default,
    },
  },
]);

export const targetItemText = style({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const emptySearchResult = style([
  {
    padding: `${space.s8}px ${space.s12}px`,
    color: themeVars.container.foreground.tertiary,
  },
  typography.bodySm,
]);

export const targetsEmpty = style([
  emptySearchResult,
  {
    gridColumn: '1 / -1',
  },
]);

export const streamField = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
});

export const targetsBox = style({
  border: `1px solid ${themeVars.container.border.default}`,
  borderTop: 0,
  borderRadius: `0 0 ${borderRadius.r8}px ${borderRadius.r8}px`,
  overflow: 'hidden',
  paddingBottom: space.s6,
});

export const targetsHeader = style([
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s8,
    padding: `${space.s8}px ${space.s12}px`,
    color: themeVars.container.foreground.secondary,
  },
  typography.labelSm,
]);

export const targetItemLabel = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s8,
  flex: 1,
  minWidth: 0,
  cursor: 'pointer',
});

export const targetRemove = style({
  marginLeft: space.s8,
  flex: 'none',
  color: themeVars.container.foreground.tertiary,
  opacity: 0,

  ':hover': {
    color: themeVars.color.text.alert.critical,
  },
  ':focus-visible': {
    opacity: 1,
  },

  selectors: {
    [`${targetRow}:hover &`]: {
      opacity: 1,
    },
  },
});

export const searchRow = style({
  padding: `${space.s8}px ${space.s12}px`,
});

export const addOption = style([
  typography.bodySm,
  {
    display: 'flex',
    alignItems: 'center',
    gap: space.s8,
    width: '100%',
    padding: `${space.s6}px ${space.s12}px`,
    border: 0,
    borderBottom: `1px solid ${themeVars.container.border.default}`,
    background: 'none',
    color: themeVars.container.foreground.brand,
    cursor: 'pointer',
    textAlign: 'left',

    ':hover': {
      backgroundColor: themeVars.controls.background.hover,
    },
  },
]);

export const addOptionTarget = style({
  color: themeVars.container.foreground.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const fieldError = style([
  typography.bodySm,
  {
    margin: 0,
    color: themeVars.color.text.alert.critical,
  },
]);

export const targetsHeaderLabel = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s4,
});

export const targetsHint = style({
  display: 'inline-flex',
  color: themeVars.container.foreground.tertiary,
  cursor: 'help',
});

export const targetsFoot = style([
  typography.bodySm,
  {
    marginTop: space.s4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s12,
    color: themeVars.container.foreground.secondary,
  },
]);

export const removalAlert = style([
  typography.bodySm,
  {
    display: 'flex',
    alignItems: 'center',
    gap: space.s12,
    color: themeVars.color.text.alert.critical,
  },
]);
